// @flow
// $FlowIssue need to update to a more recent flow version
import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';
import moment from 'moment';
import * as yup from 'yup';
import { Field, Form, Formik } from 'formik';
import { hoistStatics } from 'recompose';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';
import {
    DateRange as DateRangeIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
} from '@material-ui/icons';
import type { Dispatch } from 'redux';
import type { Connector } from 'react-redux';
import FormikDateTimePicker from '../../components/FormikDateTimePicker';
import SubmitButton from '../../components/SubmitButton';
import * as dialog from '../../ducks/dialog';
import { actions, selectors } from './duck';
import styles from './styles';
import type { State as RootState } from '../../redux/initialState';

type OwnProps = {}
type StateProps = {
    onClose: () => void,
    onSubmitFail: () => void,
    onSubmitSuccess: () => void,
    open: boolean,
    reschedulableTicketIds: number[],
    ticketIds: number[],
}
type DispatchProps = {
    registerDialog: (name: string) => void,
    submit: (values: Object) => Promise<void>,
    unregisterDialog: (name: string) => void,
};
type Props = OwnProps & StateProps & DispatchProps;

const isOutsideRange = (date: typeof moment): boolean => moment(date).isBefore(moment().startOf('day'));

const RescheduleSchema = yup.object().shape({
    startDate: yup.date()
        .test({
            message: '${path} field must be in the future', // eslint-disable-line no-template-curly-in-string
            name: 'futureDate',
            test(value) {
                const date = moment(value);
                return date.isSameOrAfter(moment());
            },
        })
        .required('validation.required'),
    ticketIds: yup.array().of(yup.number()),
});

const useStyles = makeStyles(styles, { name: 'RescheduleDialog' });

export function RescheduleDialog(props: Props) {
    const {
        onClose,
        onSubmitFail,
        onSubmitSuccess,
        open,
        registerDialog,
        reschedulableTicketIds,
        submit,
        ticketIds,
        unregisterDialog,
    } = props;

    const classes = useStyles(props);
    const { t } = useTranslation();

    useEffect(() => {
        registerDialog(RescheduleDialog.NAME);
        return () => {
            unregisterDialog(RescheduleDialog.NAME);
        };
    }, [registerDialog, unregisterDialog]);

    const rescheduleWarning = useMemo(() => {
        // @todo There may be other conditions that prevent tickets from being rescheduled
        const canRescheduleAllTickets = reschedulableTicketIds.length === ticketIds.length;
        return !canRescheduleAllTickets
            ? 'Some of the selected tickets cannot be rescheduled'
            : undefined;
    }, [reschedulableTicketIds, ticketIds]);

    const rescheduleError = useMemo(() => {
        // @todo There may be other conditions that prevent tickets from being rescheduled
        const canRescheduleSomeTickets = reschedulableTicketIds.length > 0;
        return !canRescheduleSomeTickets
            ? 'The tickets you selected cannot be rescheduled'
            : undefined;
    }, [reschedulableTicketIds]);

    const displayWarning = !rescheduleError && rescheduleWarning;
    const displayError = !!rescheduleError;

    const errorMessage = (
        <DialogContentText className={classes.error}>
            <ErrorIcon className={classes.icon} />
            <span>{rescheduleError}</span>
        </DialogContentText>
    );

    const warningMessage = (
        <DialogContentText className={classes.warning}>
            <WarningIcon className={classes.icon} />
            <span>{rescheduleWarning}</span>
        </DialogContentText>
    );

    return (
        <Formik
          enableReinitialize
          initialValues={{ ticketIds: reschedulableTicketIds }}
          onSubmit={(values, { setStatus, setSubmitting }) => {
              submit(values)
                  .then(() => {
                      setStatus({ success: true });
                      onSubmitSuccess();
                  })
                  .catch((error) => {
                      setStatus({ error });
                      onSubmitFail();
                  })
                  .then(() => setSubmitting(false));
          }}
          validationSchema={RescheduleSchema}
        >
            {(formikProps: Object) => {
                const { isSubmitting, isValid, handleReset, handleSubmit } = formikProps;
                return (
                    <Dialog open={open} onClose={onClose} onExited={handleReset}>
                        <Form onSubmit={handleSubmit}>
                            <DialogTitle>{t('rescheduleDialog.title')}</DialogTitle>
                            <DialogContent className={cx({ [classes.disablePaddingBottom]: displayError || displayWarning })}>
                                <DialogContentText>
                                    {t('rescheduleDialog.text', { count: ticketIds.length })}
                                </DialogContentText>
                                <Field
                                  name="startDate"
                                  component={FormikDateTimePicker}
                                  label={t('rescheduleDialog.startDate')}
                                  isOutsideRange={isOutsideRange}
                                  customInputIcon={<DateRangeIcon />}
                                />
                                {displayWarning ? warningMessage : null}
                                {displayError ? errorMessage : null}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={onClose}>
                                    {t('rescheduleDialog.cancel')}
                                </Button>
                                <SubmitButton
                                  className={classes.submitButton}
                                  color="primary"
                                  disabled={isSubmitting || !isValid}
                                  submitting={isSubmitting}
                                  type="submit"
                                  variant="contained"
                                >
                                    {t('rescheduleDialog.confirm')}
                                </SubmitButton>
                            </DialogActions>
                        </Form>
                    </Dialog>
                );
            }}
        </Formik>
    );
}

RescheduleDialog.NAME = 'reschedule';
RescheduleDialog.defaultProps = {
    onClose: () => {},
    onSubmitFail: () => {},
    onSubmitSuccess: () => {},
    open: false,
    ticketIds: [],
};

const mapStateToProps = (state: RootState): StateProps => {
    const props = {
        ...RescheduleDialog.defaultProps,
        ...state.dialog.registered[RescheduleDialog.NAME],
    };
    return {
        ...props,
        reschedulableTicketIds: selectors.getReschedulableTicketIds(state, props),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
    registerDialog: (name: string) => dispatch(dialog.actions.register(name)),
    submit: (values: Object) => dispatch(actions.submit(values)),
    unregisterDialog: (name: string) => dispatch(dialog.actions.unregister(name)),
});

const connector: Connector<OwnProps, Props> = connect(mapStateToProps, mapDispatchToProps);
export default hoistStatics(connector)(RescheduleDialog);
